import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { Box, Alert, IconButton } from '@mui/joy';
import {
  CheckCircle as SuccessIcon,
  X as CloseIcon
} from '@phosphor-icons/react';
import { PageHeader } from '../../components/common/PageHeader';

export const MarketingApp: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const intros: Record<string, { title: string; description: string }> = {
    reviews: { title: 'Reviews', description: 'Read customer feedback, send a review request, or set up your review funnel to collect more reviews.' },
    'social-media': { title: 'Social media', description: 'Connect your accounts, then create a post or schedule content for the days ahead.' },
    content: { title: 'Content marketing', description: 'Start with a topical map, choose an article idea, and track content from draft to publication.' },
    forms: { title: 'Forms', description: 'Add a form to collect leads, then embed it on your website. Open an existing form to edit it or review submissions.' },
    'qr-codes': { title: 'QR codes', description: 'Create a code for a website, review request, or promotion. Download it for print and track scans here.' },
    'media-library': { title: 'Media library', description: 'Upload photos and videos, organize them into collections, and find media to reuse across your marketing.' },
    'email-marketing': { title: 'Email marketing', description: 'Create a campaign, choose an audience, and review your message before sending. Use drip campaigns for a series of follow-ups.' },
    settings: { title: 'Marketing settings', description: 'Connect Google Business Profile and choose the keywords and competitors you want to track.' },
  };
  const page = location.pathname.split('/')[2];
  const intro = intros[page];
  // These workspaces already render their own title, description and actions.
  const hasOwnHeading = ['direct-mail', 'local-seo', 'website-builder', 'roof-quote-widget'].includes(page);

  // Check for success message in URL parameters
  useEffect(() => {
    const success = searchParams.get('success');
    if (success === 'gbp_connected') {
      setSuccessMessage('Google Business Profile connected successfully!');
      // Remove the success parameter from URL
      searchParams.delete('success');
      setSearchParams(searchParams, { replace: true });
      
      // Clear the message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    }
  }, [searchParams, setSearchParams]);

  return (
    <Box sx={{ width: '100%', minWidth: 0, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {!hasOwnHeading && <PageHeader section="Marketing" title={intro?.title || 'Marketing'} description={intro?.description} />}

      {successMessage && (
        <Alert
          color="success"
          startDecorator={<SuccessIcon />}
          sx={{ mb: 3 }}
          endDecorator={
            <IconButton 
              variant="plain" 
              size="sm" 
              color="success"
              onClick={() => setSuccessMessage(null)}
              aria-label="Dismiss confirmation"
            >
              <CloseIcon />
            </IconButton>
          }
        >
          {successMessage}
        </Alert>
      )}
      
      <Outlet />
    </Box>
  );
};